<template>
  <v-dialog v-model="dialog" max-width="700" persistent>
    <v-card class="product-switcher-card">
      <!-- Header -->
      <v-card-title class="dialog-header pa-0">
        <div class="header-content">
          <div class="header-icon-wrapper">
            <v-icon size="24" color="white">mdi-apps</v-icon>
          </div>
          <div class="header-text">
            <h2 class="header-title">Switch Product</h2>
            <span class="header-subtitle">Select a product to navigate</span>
          </div>
        </div>
        <v-btn icon class="close-btn" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Tabs -->
      <v-tabs v-model="activeTab" class="product-tabs" grow>
        <v-tab class="product-tab tab-starr">
          <v-icon left size="20">mdi-star-circle</v-icon>
          STARR
          <v-chip v-if="starrProducts.length > 0" x-small class="ml-2 tab-count">{{ starrProducts.length }}</v-chip>
        </v-tab>
        <v-tab class="product-tab tab-trade">
          <v-icon left size="20">mdi-swap-horizontal-bold</v-icon>
          TRADE
          <v-chip v-if="tradeProducts.length > 0" x-small class="ml-2 tab-count">{{ tradeProducts.length }}</v-chip>
        </v-tab>
        <v-tab class="product-tab tab-jewel">
          <v-icon left size="20">mdi-diamond-stone</v-icon>
          JEWEL
          <v-chip v-if="jewelProducts.length > 0" x-small class="ml-2 tab-count">{{ jewelProducts.length }}</v-chip>
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-card-text class="pa-5">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
          <p class="loading-text">Loading products...</p>
        </div>

        <!-- Tab Content -->
        <v-tabs-items v-else v-model="activeTab">
          <!-- STARR Tab -->
          <v-tab-item>
            <div v-if="starrProducts.length === 0" class="empty-state">
              <div class="empty-icon-wrapper starr-empty">
                <v-icon size="48" color="white">mdi-star-circle</v-icon>
              </div>
              <p class="empty-title">No STARR Products</p>
              <p class="empty-desc">You don't have access to any STARR products</p>
            </div>
            <div v-else class="products-container">
              <div
                v-for="product in starrProducts"
                :key="product.user_id + '-' + product.product"
                class="product-item"
                :class="{
                  'product-current': isCurrentProduct(product),
                  'product-clickable': !isCurrentProduct(product)
                }"
                @click="!isCurrentProduct(product) && switchToProduct(product)"
              >
                <div class="product-content">
                  <div class="product-icon" :class="getProductIconClass(product.product)">
                    <v-icon size="24" color="white">{{ getProductIcon(product.product) }}</v-icon>
                  </div>
                  <div class="product-details">
                    <div class="product-header">
                      <span class="product-name">{{ getProductDisplayName(product.product) }}</span>
                      <v-chip v-if="isCurrentProduct(product)" x-small color="#10b981" text-color="white" class="current-badge">
                        <v-icon left x-small>mdi-check</v-icon>
                        Current
                      </v-chip>
                    </div>
                    <span class="product-desc">{{ product.descriptions }}</span>
                    <div class="product-meta">
                      <v-icon x-small color="#A1A5B7">mdi-domain</v-icon>
                      <span>{{ product.company_name }}</span>
                      <span class="meta-separator">|</span>
                      <v-icon x-small color="#A1A5B7">mdi-web</v-icon>
                      <span>{{ product.ref_domain }}</span>
                    </div>
                  </div>
                  <div class="product-action">
                    <div v-if="switchingTo === product.product + product.ref_domain" class="switching-loader">
                      <v-progress-circular indeterminate size="24" width="2" color="primary"></v-progress-circular>
                    </div>
                    <div v-else-if="!isCurrentProduct(product)" class="action-arrow">
                      <v-icon color="#7E8299">mdi-arrow-right-circle-outline</v-icon>
                    </div>
                  </div>
                </div>
                <v-progress-linear v-if="switchingTo === product.product + product.ref_domain" indeterminate color="primary" height="3" class="switching-progress"></v-progress-linear>
              </div>
            </div>
          </v-tab-item>

          <!-- TRADE Tab -->
          <v-tab-item>
            <div v-if="tradeProducts.length === 0" class="empty-state">
              <div class="empty-icon-wrapper trade-empty">
                <v-icon size="48" color="white">mdi-swap-horizontal-bold</v-icon>
              </div>
              <p class="empty-title">No TRADE Products</p>
              <p class="empty-desc">You don't have access to any TRADE products</p>
            </div>
            <div v-else class="products-container">
              <div
                v-for="product in tradeProducts"
                :key="product.user_id + '-' + product.product"
                class="product-item"
                :class="{
                  'product-current': isCurrentProduct(product),
                  'product-clickable': !isCurrentProduct(product)
                }"
                @click="!isCurrentProduct(product) && switchToProduct(product)"
              >
                <div class="product-content">
                  <div class="product-icon" :class="getProductIconClass(product.product)">
                    <v-icon size="24" color="white">{{ getProductIcon(product.product) }}</v-icon>
                  </div>
                  <div class="product-details">
                    <div class="product-header">
                      <span class="product-name">{{ getProductDisplayName(product.product) }}</span>
                      <v-chip v-if="isCurrentProduct(product)" x-small color="#10b981" text-color="white" class="current-badge">
                        <v-icon left x-small>mdi-check</v-icon>
                        Current
                      </v-chip>
                    </div>
                    <span class="product-desc">{{ product.descriptions }}</span>
                    <div class="product-meta">
                      <v-icon x-small color="#A1A5B7">mdi-domain</v-icon>
                      <span>{{ product.company_name }}</span>
                      <span class="meta-separator">|</span>
                      <v-icon x-small color="#A1A5B7">mdi-web</v-icon>
                      <span>{{ product.ref_domain }}</span>
                    </div>
                  </div>
                  <div class="product-action">
                    <div v-if="switchingTo === product.product + product.ref_domain" class="switching-loader">
                      <v-progress-circular indeterminate size="24" width="2" color="primary"></v-progress-circular>
                    </div>
                    <div v-else-if="!isCurrentProduct(product)" class="action-arrow">
                      <v-icon color="#7E8299">mdi-arrow-right-circle-outline</v-icon>
                    </div>
                  </div>
                </div>
                <v-progress-linear v-if="switchingTo === product.product + product.ref_domain" indeterminate color="primary" height="3" class="switching-progress"></v-progress-linear>
              </div>
            </div>
          </v-tab-item>

          <!-- JEWEL Tab -->
          <v-tab-item>
            <div v-if="jewelProducts.length === 0" class="empty-state">
              <div class="empty-icon-wrapper jewel-empty">
                <v-icon size="48" color="white">mdi-diamond-stone</v-icon>
              </div>
              <p class="empty-title">No JEWEL Products</p>
              <p class="empty-desc">You don't have access to any JEWEL products</p>
            </div>
            <div v-else class="products-container">
              <div
                v-for="product in jewelProducts"
                :key="product.user_id + '-' + product.product"
                class="product-item"
                :class="{
                  'product-current': isCurrentProduct(product),
                  'product-clickable': !isCurrentProduct(product)
                }"
                @click="!isCurrentProduct(product) && switchToProduct(product)"
              >
                <div class="product-content">
                  <div class="product-icon" :class="getProductIconClass(product.product)">
                    <v-icon size="24" color="white">{{ getProductIcon(product.product) }}</v-icon>
                  </div>
                  <div class="product-details">
                    <div class="product-header">
                      <span class="product-name">{{ getProductDisplayName(product.product) }}</span>
                      <v-chip v-if="isCurrentProduct(product)" x-small color="#10b981" text-color="white" class="current-badge">
                        <v-icon left x-small>mdi-check</v-icon>
                        Current
                      </v-chip>
                    </div>
                    <span class="product-desc">{{ product.descriptions }}</span>
                    <div class="product-meta">
                      <v-icon x-small color="#A1A5B7">mdi-domain</v-icon>
                      <span>{{ product.company_name }}</span>
                      <span class="meta-separator">|</span>
                      <v-icon x-small color="#A1A5B7">mdi-web</v-icon>
                      <span>{{ product.ref_domain }}</span>
                    </div>
                  </div>
                  <div class="product-action">
                    <div v-if="switchingTo === product.product + product.ref_domain" class="switching-loader">
                      <v-progress-circular indeterminate size="24" width="2" color="primary"></v-progress-circular>
                    </div>
                    <div v-else-if="!isCurrentProduct(product)" class="action-arrow">
                      <v-icon color="#7E8299">mdi-arrow-right-circle-outline</v-icon>
                    </div>
                  </div>
                </div>
                <v-progress-linear v-if="switchingTo === product.product + product.ref_domain" indeterminate color="primary" height="3" class="switching-progress"></v-progress-linear>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ProductSwitcherDialog",

  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      products: [],
      switchingTo: null,
      currentDomain: localStorage.getItem("domain"),
      activeTab: 0
    };
  },

  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },

    present_product(){
      if (localStorage.getItem("is_family_book") == 'true') {
        return 'STARR_FamilyBook'
      }
      if (localStorage.getItem("is_boardpage") == 'true') {
        return 'STARR_Board'
      }
      return 'STARR';
    },

    // Filter products by category
    starrProducts() {
      return this.products
        .filter(p => p.product === 'STARR' || p.product === 'STARR_Board' || p.product === 'STARR_FamilyBook')
        .sort((a, b) => {
          const aIsCurrent = this.isCurrentProduct(a);
          const bIsCurrent = this.isCurrentProduct(b);
          if (aIsCurrent && !bIsCurrent) return -1;
          if (!aIsCurrent && bIsCurrent) return 1;
          return 0;
        });
    },

    tradeProducts() {
      return this.products
        .filter(p => p.product === 'TRADE')
        .sort((a, b) => {
          const aIsCurrent = this.isCurrentProduct(a);
          const bIsCurrent = this.isCurrentProduct(b);
          if (aIsCurrent && !bIsCurrent) return -1;
          if (!aIsCurrent && bIsCurrent) return 1;
          return 0;
        });
    },

    jewelProducts() {
      return this.products
        .filter(p => p.product === 'JEWEL')
        .sort((a, b) => {
          const aIsCurrent = this.isCurrentProduct(a);
          const bIsCurrent = this.isCurrentProduct(b);
          if (aIsCurrent && !bIsCurrent) return -1;
          if (!aIsCurrent && bIsCurrent) return 1;
          return 0;
        });
    }
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.currentDomain = localStorage.getItem("domain");
        this.fetchProducts();
        this.setActiveTabBasedOnCurrentProduct();
      }
    }
  },

  methods: {
    closeDialog() {
      this.dialog = false;
    },

    isCurrentProduct(product) {
      return product.product === this.present_product && product.ref_domain === this.currentDomain;
    },

    setActiveTabBasedOnCurrentProduct() {
      // Set active tab based on current product
      if (this.present_product === 'TRADE') {
        this.activeTab = 1;
      } else if (this.present_product === 'JEWEL') {
        this.activeTab = 2;
      } else {
        this.activeTab = 0; // STARR, STARR_Board, STARR_FamilyBook
      }
    },

    getProductDisplayName(product) {
      const names = {
        'STARR': 'STARR',
        'STARR_FamilyBook': 'Family Book',
        'STARR_Board': 'Board',
        'JEWEL': 'JEWEL',
        'TRADE': 'TRADE'
      };
      return names[product] || product;
    },

    async fetchProducts() {
      this.loading = true;
      try {
        const email = localStorage.getItem("USER_EMAIL");

        const response = await this.$ssoApi.get('/api/sso/products', {
          params: {
            email: email
          }
        });

        if (response.data.success) {
          this.products = response.data.products || [];
        } else {
          this.products = [];
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        this.products = [];
      } finally {
        this.loading = false;
      }
    },

    getProductIcon(product) {
      const icons = {
        'STARR': 'mdi-star-circle',
        'STARR_FamilyBook': 'mdi-book-account',
        'STARR_Board': 'mdi-view-dashboard',
        'JEWEL': 'mdi-diamond-stone',
        'TRADE': 'mdi-swap-horizontal-bold'
      };
      return icons[product] || 'mdi-application';
    },

    getProductIconClass(product) {
       const classes = {
        'STARR': 'icon-starr',
        'STARR_FamilyBook': 'icon-starr-familybook',
        'STARR_Board': 'icon-starr-board',
        'JEWEL': 'icon-jewel',
        'TRADE': 'icon-trade'
      };
      return classes[product] || 'icon-default';
    },

    async switchToProduct(product) {
      if (this.isCurrentProduct(product)) return;

      this.switchingTo = product.product + product.ref_domain;

      try {
        const response = await this.$ssoApi.post('/api/sso/generate_token', {
          email: localStorage.getItem("USER_EMAIL"),
          user_id: product.user_id,
          target_product: product.product,
          redirection_url: product.redirection_url,
          source_product: this.present_product,
          ref_domain: product.ref_domain,
          ref_schema_name: product.ref_schema_name
        });

        if (response.data.success) {
          const redirectUrl = response.data.redirect_url;
          window.open(redirectUrl, '_blank');
          this.$toast.success(`Opening ${this.getProductDisplayName(product.product)}...`);
          this.closeDialog();
        } else {
          this.$toast.error(response.data.error || 'Failed to generate SSO token');
        }
      } catch (error) {
        console.error('Error switching product:', error);
        this.$toast.error('Failed to switch product');
      } finally {
        this.switchingTo = null;
      }
    }
  }
};
</script>

<style scoped>
.product-switcher-card {
  border-radius: 16px !important;
  overflow: hidden;
}

/* Header Styles */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px !important;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.header-text {
  display: flex;
  flex-direction: column;
}

.header-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.header-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.close-btn {
  color: #64748b !important;
}

/* Tabs Styles */
.product-tabs {
  background: #f8fafc !important;
}

.product-tab {
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 14px !important;
}

.tab-starr {
  color: #2A167D !important;
}

.tab-trade {
  color: #00BCD4 !important;
}

.tab-jewel {
  color: #E91E63 !important;
}

.tab-count {
  background: #e2e8f0 !important;
  color: #475569 !important;
  font-size: 10px !important;
  height: 18px !important;
  min-width: 18px !important;
}

.v-tab--active.tab-starr {
  background: rgba(42, 22, 125, 0.08) !important;
}

.v-tab--active.tab-trade {
  background: rgba(0, 188, 212, 0.08) !important;
}

.v-tab--active.tab-jewel {
  background: rgba(233, 30, 99, 0.08) !important;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.loading-text {
  margin-top: 16px;
  color: #64748b;
  font-size: 14px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.empty-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.starr-empty {
  background: linear-gradient(135deg, #2A167D 0%, #2A167D 100%) !important;
}

.trade-empty {
  background: linear-gradient(135deg, #00BCD4 0%, #22d3d1 100%) !important;
}

.jewel-empty {
  background: linear-gradient(135deg, #E91E63 0%, #f472b6 100%) !important;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}

.empty-desc {
  font-size: 14px;
  color: #94a3b8;
}

/* Products Container */
.products-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
}

/* Product Item */
.product-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
}

.product-clickable {
  cursor: pointer;
}

.product-clickable:hover {
  background: #fff;
  border-color: #c7d2fe;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.12);
  transform: translateY(-2px);
}

.product-clickable:hover .action-arrow .v-icon {
  color: #6366f1 !important;
}

.product-current {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-color: #86efac;
  cursor: default;
}

.product-content {
  display: flex;
  align-items: center;
  padding: 18px 20px;
  gap: 18px;
}

/* Product Icon */
.product-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.icon-starr {
  background: linear-gradient(135deg, #2A167D 0%, #2A167D 100%) !important;
}

.icon-starr-familybook {
  background: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%) !important;
}

.icon-starr-board {
  background: linear-gradient(135deg, #FFA500 0%, #ffbe4d 100%) !important;
}

.icon-jewel {
  background: linear-gradient(135deg, #E91E63 0%, #f472b6 100%) !important;
}

.icon-trade {
  background: linear-gradient(135deg, #00BCD4 0%, #22d3d1 100%) !important;
}

.icon-default {
  background: linear-gradient(135deg, #64748b 0%, #94a3b8 100%) !important;
}

/* Product Details */
.product-details {
  flex: 1;
  min-width: 0;
}

.product-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.product-name {
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
}

.current-badge {
  font-size: 10px !important;
  font-weight: 600;
  height: 20px !important;
}

.product-desc {
  font-size: 13px;
  color: #64748b;
  display: block;
  margin-bottom: 6px;
}

.product-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #94a3b8;
}

.meta-separator {
  color: #cbd5e1;
}

/* Product Action */
.product-action {
  flex-shrink: 0;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-arrow {
  transition: transform 0.2s ease;
}

.product-clickable:hover .action-arrow {
  transform: translateX(4px);
}

.switching-loader {
  display: flex;
  align-items: center;
  justify-content: center;
}

.switching-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

/* Responsive */
@media (max-width: 600px) {
  .dialog-header {
    padding: 16px !important;
  }

  .header-icon-wrapper {
    width: 40px;
    height: 40px;
  }

  .header-title {
    font-size: 18px;
  }

  .product-content {
    padding: 14px 16px;
    gap: 14px;
  }

  .product-icon {
    width: 44px;
    height: 44px;
  }

  .product-name {
    font-size: 15px;
  }

  .product-meta {
    flex-wrap: wrap;
  }

  .product-tab {
    font-size: 12px !important;
    padding: 0 8px !important;
  }

  .tab-count {
    display: none !important;
  }
}
</style>
